import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { _internal, shell } from '@ey-fso-tax/mfe-sdk';
import {
  createAppInsightsLogger,
  recommendedAppInsightsConfig,
  type AppInsightsLogger,
  type TelemetryContext,
} from '@ey-fso-tax/mfe-sdk/appinsights';
import { authModule } from '@/auth/auth-module';
import { msalInstance } from '@/auth/msal';
import { useClientStore } from '@/client/client-store';
import { getCurrentUserType } from '@/auth/user-type';

/**
 * Telemetry wiring.
 *
 * The shell owns the App Insights resource; `shell.logger` is the API both the
 * shell and every MFE call. This module creates the transport, hands it the
 * portal's identity context and registers it in the SDK registry — which is
 * what finally gives downstream apps a way to log at all, since the shell
 * exposes no telemetry window global.
 *
 * `getTracker()` survives as a thin adapter over `shell.logger` so the existing
 * call sites keep working untouched.
 */

/** Separates this shell from the Angular one (`RAPToR-SHELL-UI`) in queries. */
const ROLE_NAME = 'RAPToR-SHELL-UI-REACT';
const APP_ID = 'SHELL-UI-REACT';

export interface Tracker {
  event(name: string, properties?: Record<string, unknown>): void;
  metric(name: string, value: number, properties?: Record<string, unknown>): void;
  error(error: Error, properties?: Record<string, unknown>): void;
  pageView(name: string, uri?: string, properties?: Record<string, unknown>): void;
}

let logger: AppInsightsLogger | undefined;

/**
 * Ingestion dial, in percent. Unset, non-numeric or out of range means "send
 * everything" — the release pipeline substitutes `#{...}#` tokens into env
 * files, so a token nobody defined must not be read as a sampling rate.
 */
function resolveSamplingPercentage(): number {
  const raw = import.meta.env.VITE_APPINSIGHTS_SAMPLING_PERCENTAGE;
  if (!raw) return 100;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed <= 0 || parsed > 100) return 100;
  return parsed;
}

/**
 * Resolved per record rather than captured once: telemetry initialises before
 * MSAL, so anything read at construction time is empty.
 */
function resolveContext(): TelemetryContext {
  const user = authModule.user;
  const { current, bootstrapClientId } = useClientStore.getState();

  return {
    userEmail: user?.email,
    userName: user?.name ?? user?.email,
    userGuid: user?.oid,
    userType: getCurrentUserType() ?? undefined,
    userStatus: 'Active',
    // `current` stays null until the client list resolves; the bootstrap id is
    // seeded from the URL or localStorage and is the only id available cold.
    clientId: current?.id ?? bootstrapClientId ?? undefined,
    clientName: current?.name,
  };
}

export function initializeTracker(): void {
  const connectionString = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING;
  if (!connectionString) return;

  // Session renewal: Angular's QA uses 86400000 (24h); default 30 min.
  const sessionRenewalMs = import.meta.env.VITE_APPINSIGHTS_SESSION_RENEWAL_MS
    ? Number(import.meta.env.VITE_APPINSIGHTS_SESSION_RENEWAL_MS)
    : undefined;

  const ai = new ApplicationInsights({
    config: {
      connectionString,
      // Route tracking, page-visit time, dependency auto-tracking and CORS
      // correlation are all pinned by the SDK baseline; the rationale for each
      // lives with it rather than being restated per host.
      ...recommendedAppInsightsConfig({ sessionRenewalMs }),
      samplingPercentage: resolveSamplingPercentage(),
    },
  });
  ai.loadAppInsights();

  logger = createAppInsightsLogger({
    ai,
    appId: APP_ID,
    roleName: ROLE_NAME,
    getContext: resolveContext,
    // Angular stamped the sign-in address onto `context.user.id`, which is what
    // the Users and Sessions blades count by.
    userIdSource: 'email',
    isAuthenticated: () => msalInstance.getActiveAccount() !== null,
  });

  // Registering here rather than in `registerShellSdk()` is deliberate:
  // telemetry starts before MSAL, and an MFE that mounts early should reach a
  // real transport instead of the SDK's console fallback.
  _internal.register('logger', logger);
}

/**
 * Sends records raised before sign-in. Exceptions are never buffered, so this
 * only ever releases events, traces, metrics and page views.
 */
export function flushTelemetry(): void {
  logger?.flush();
}

const tracker: Tracker = {
  event: (name, properties) => shell.logger.event(name, properties),
  metric: (name, value, properties) => shell.logger.metric(name, value, properties),
  error: (error, properties) => shell.logger.exception(error, properties),
  pageView: (name, uri, properties) => shell.logger.pageView(name, uri, properties),
};

export function getTracker(): Tracker {
  return tracker;
}

/**
 * Clear the App Insights session on login — forces a fresh telemetry session,
 * so one browser session shared by two users does not merge into one App
 * Insights session. Unlike the Angular `clearSession()` this cannot throw: the
 * transport applies each step independently.
 */
export function clearTelemetrySession(): void {
  logger?.clearSession();
}
